import { config } from "../Environment";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RagProductSearchRequest {
  query: string;
  filters?: {
    type?: string;
    categories?: string[];
    tags?: string[];
    vendorId?: string;
  };
  limit?: number;
  includeExplanations?: boolean;
}

export interface RagProductResult {
  _id: string;
  name: string;
  description?: string;
  type: string;
  score: number;
  data?: any;
  fiVendor?: any;
  vendorName?: string;
  [key: string]: any;
}

export interface RagProductSearchResponse {
  results: RagProductResult[];
  count: number;
  /** Maps result _id → one-sentence reason it matches (top results only) */
  explanations?: Record<string, string> | null;
}

export interface RagIdentitySearchRequest {
  identitySpaceId: string;
  traitId?: string;
  limit?: number;
  includeExplanation?: boolean;
}

export interface RagIdentitySearchResponse {
  products: RagProductResult[];
  prescriptions: any[];
  identityContext: any;
  focusedTrait?: { _id: string; name: string; category: string };
  searchQuery: string;
  traitMatches?: any;
  explanation?: string | null;
}

export interface RagReindexRequest {
  scope?: "all" | "products" | "prescriptions";
}

export interface RagReindexResponse {
  message: string;
  results: {
    products?: {
      total: number;
      updated: number;
      skipped: number;
      errors: number;
    };
    prescriptions?: {
      total: number;
      updated: number;
      skipped: number;
      errors: number;
    };
  };
}

// ─── API Functions ───────────────────────────────────────────────────────────

/**
 * Search products using RAG semantic search
 */
export const searchProducts = async (
  retryFetch: Function,
  data: RagProductSearchRequest
): Promise<RagProductSearchResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/rag/search/products`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to search products");
  }

  return response.json();
};

/**
 * Search products/prescriptions relevant to an identity space (optionally
 * focused on a single trait). The backend reformulates the identity/trait
 * into a semantic query before searching.
 */
export const searchIdentity = async (
  retryFetch: Function,
  data: RagIdentitySearchRequest
): Promise<RagIdentitySearchResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/rag/search/identity`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to search for identity");
  }

  return response.json();
};

/**
 * Trigger embedding reindex for products and/or prescriptions
 */
export const reindexEmbeddings = async (
  retryFetch: Function,
  data: RagReindexRequest
): Promise<RagReindexResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/rag/reindex`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to reindex embeddings");
  }

  return response.json();
};
